import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  Box, 
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Divider,
  CircularProgress
} from '@mui/material';
import axios from 'axios';

// Import our modular components
import KeywordLayout from './Layout/KeywordLayout';
import KeywordHeader from './Components/KeywordHeader';
import KeywordTable from './Components/KeywordTable';
import KeywordStats from './Components/KeywordStats';
import AddKeywordDialog from './Components/AddKeywordDialog';
import FilterDialog from './Components/FilterDialog';

const KeywordPanel = () => {
  // State for keywords data
  const [keywords, setKeywords] = useState([]);
  const [filteredKeywords, setFilteredKeywords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(false);
  
  // State for search and filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState(['active']);
  const [filters, setFilters] = useState({
    status: 'all',
    performance: 'all',
    category: 'all',
    sortBy: 'keyword',
    payoutRange: [0, 1]
  });
  
  // State for dialogs
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openFilterDialog, setOpenFilterDialog] = useState(false);
  const [openStatsDialog, setOpenStatsDialog] = useState(false);
  const [selectedKeyword, setSelectedKeyword] = useState(null);
  const [keywordStats, setKeywordStats] = useState(null);
  
  // State for notifications
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // API base URL
  const API_URL = 'mediabuying/keywords';

  // Fetch keywords from API
  const fetchKeywords = useCallback(async (params = {}) => {
    setLoading(true);
    try {
      const response = await axios.get(API_URL, { params });
      setKeywords(response.data);
      setFilteredKeywords(response.data);
    } catch (error) {
      console.error('Error fetching keywords:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load keywords. Please try again.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [API_URL]);

  // Initial data load
  useEffect(() => {
    fetchKeywords();
  }, [fetchKeywords]);

  // Apply filters when search or status filter changes
  useEffect(() => {
    filterKeywords();
  }, [searchQuery, statusFilter, keywords]);

  // Function to filter keywords based on search and status
  const filterKeywords = useCallback(() => {
    if (!keywords.length) return;
    
    let filtered = [...keywords];
    
    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(keyword => 
        keyword.keyword.toLowerCase().includes(query)
      );
    }
    
    // Apply status filter
    if (statusFilter && statusFilter.length > 0 && !statusFilter.includes('all')) {
      filtered = filtered.filter(keyword => statusFilter.includes(keyword.status));
    }
    
    // Apply category filter
    if (filters.category && filters.category !== 'all') {
      filtered = filtered.filter(keyword => 
        keyword.category === filters.category
      );
    }
    
    setFilteredKeywords(filtered);
  }, [keywords, searchQuery, statusFilter, filters.category]);

  // Function to apply advanced filters
  const applyAdvancedFilters = useCallback(async () => {
    setLoading(true);
    
    // Prepare filter parameters
    const params = {};
    
    if (filters.status !== 'all') {
      params.status = filters.status;
    }
    
    if (filters.category !== 'all') {
      params.category = filters.category;
    }
    
    if (filters.performance !== 'all') {
      params.performance = filters.performance;
    }
    
    // Add payout range filter - ensure they are sent as numbers
    params.payoutMin = parseFloat(filters.payoutRange[0]);
    params.payoutMax = parseFloat(filters.payoutRange[1]);
    
    // Add sorting
    if (filters.sortBy) {
      params.sortBy = filters.sortBy;
    }
    
    // Add search query if present
    if (searchQuery) {
      params.search = searchQuery;
    }
    
    console.log('Applying filters:', params); // Debug log
    
    try {
      const response = await axios.get(API_URL, { params });
      console.log('Filter response:', response.data); // Debug log
      
      // Update state with filtered data
      setKeywords(response.data);
      setFilteredKeywords(response.data);
      setOpenFilterDialog(false);
    } catch (error) {
      console.error('Error applying filters:', error);
      setSnackbar({
        open: true,
        message: 'Error applying filters. Please try again.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [API_URL, filters, searchQuery]);

  // Function to add a new keyword
  const handleAddKeyword = useCallback(async (newKeywordData) => {
    try {
      let response;
      
      if (newKeywordData.keywordType === 'bulk') {
        // Handle bulk import
        const keywords = newKeywordData.bulkKeywords
          .split('\n')
          .map(k => k.trim())
          .filter(k => k.length > 0);
        
        response = await axios.post(`${API_URL}/bulk-import`, {
          keywords,
          status: newKeywordData.status,
          category: newKeywordData.category
        });
        
        setSnackbar({
          open: true,
          message: `${response.data.length} keywords added successfully`,
          severity: 'success'
        });
      } else {
        // Handle single keyword - simplified to only include essential fields
        response = await axios.post(API_URL, {
          keyword: newKeywordData.keyword,
          status: newKeywordData.status,
          category: newKeywordData.category
        });
        
        setSnackbar({
          open: true,
          message: `Keyword "${response.data.keyword}" added successfully`,
          severity: 'success'
        });
      }
      
      // Refresh keywords list
      fetchKeywords();
      
      // Close dialog
      setOpenAddDialog(false);
    } catch (error) {
      console.error('Error adding keyword:', error);
      setSnackbar({
        open: true,
        message: 'Failed to add keyword. Please try again.',
        severity: 'error'
      });
    }
  }, [API_URL, fetchKeywords]);

  // Function to delete a keyword
  const handleDeleteKeyword = useCallback(async (id) => {
    try {
      const keywordToDelete = keywords.find(k => k.id === id);
      await axios.delete(`${API_URL}/${id}`);
      
      // Refresh keywords list
      fetchKeywords();
      
      setSnackbar({
        open: true,
        message: `Keyword "${keywordToDelete.keyword}" deleted successfully`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Error deleting keyword:', error);
      setSnackbar({
        open: true,
        message: 'Failed to delete keyword. Please try again.',
        severity: 'error'
      });
    }
  }, [API_URL, fetchKeywords, keywords]);

  // Function to toggle keyword status
  const handleToggleStatus = useCallback(async (id, currentStatus) => {
    try {
      const keywordToUpdate = keywords.find(k => k.id === id);
      const response = await axios.patch(`${API_URL}/${id}/toggle-status`);
      
      // Refresh keywords list
      fetchKeywords();
      
      const newStatus = response.data.status;
      
      setSnackbar({
        open: true,
        message: `Keyword "${keywordToUpdate.keyword}" ${newStatus === 'active' ? 'activated' : 'paused'} successfully`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Error toggling keyword status:', error);
      setSnackbar({
        open: true,
        message: 'Failed to update keyword status. Please try again.',
        severity: 'error'
      });
    }
  }, [API_URL, fetchKeywords, keywords]);

  // Function to view keyword stats
  const handleViewStats = useCallback(async (keyword) => {
    setSelectedKeyword(keyword);
    setStatsLoading(true);
    
    try {
      const response = await axios.get(`${API_URL}/${keyword.id}`);
      setKeywordStats(response.data.stats);
      setOpenStatsDialog(true);
    } catch (error) {
      console.error('Error fetching keyword stats:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load keyword statistics. Please try again.',
        severity: 'error'
      });
    } finally {
      setStatsLoading(false);
    }
  }, [API_URL]);

  // Function to close snackbar
  const handleCloseSnackbar = useCallback(() => {
    setSnackbar({ ...snackbar, open: false });
  }, [snackbar]);

  // Memoize the KeywordTable component to prevent unnecessary re-renders
  const memoizedKeywordTable = useMemo(() => (
    <KeywordTable 
      keywords={filteredKeywords}
      loading={loading}
      onViewStats={handleViewStats}
      onDeleteKeyword={handleDeleteKeyword}
      onToggleStatus={handleToggleStatus}
    />
  ), [filteredKeywords, loading, handleViewStats, handleDeleteKeyword, handleToggleStatus]);

  return (
    <KeywordLayout>
      {/* Header with search and filters */}
      <KeywordHeader 
        onAddKeyword={() => setOpenAddDialog(true)}
        onOpenFilter={() => setOpenFilterDialog(true)}
        searchQuery={searchQuery}
        onSearchChange={(e) => setSearchQuery(e.target.value)}
        statusFilter={statusFilter}
        onStatusFilterChange={(e) => setStatusFilter(e.target.value)}
      />
      
      {/* Keywords Table */}
      <Box sx={{ mt: 3 }}>
        {memoizedKeywordTable}
      </Box>
      
      {/* Add Keyword Dialog */}
      <AddKeywordDialog 
        open={openAddDialog}
        onClose={() => setOpenAddDialog(false)}
        onAddKeyword={handleAddKeyword}
      />
      
      {/* Filter Dialog */}
      <FilterDialog 
        open={openFilterDialog}
        onClose={() => setOpenFilterDialog(false)}
        filters={filters}
        setFilters={setFilters}
        onApplyFilters={applyAdvancedFilters}
      />
      
      {/* Keyword Stats Dialog */}
      <Dialog 
        open={openStatsDialog} 
        onClose={() => setOpenStatsDialog(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { borderRadius: '12px' }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h5" component="div" sx={{ fontWeight: 600 }}>
            Keyword Stats: {selectedKeyword?.keyword}
          </Typography>
        </DialogTitle>
        <Divider />
        <DialogContent>
          {statsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : keywordStats && selectedKeyword ? (
            <KeywordStats 
              keyword={selectedKeyword}
              stats={keywordStats}
            />
          ) : (
            <Typography color="text.secondary">No stats available for this keyword.</Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setOpenStatsDialog(false)} variant="outlined">Close</Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity} 
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </KeywordLayout>
  );
};

export default KeywordPanel; 